import { yakTrailsApi } from "@games-monorepo/shared-api/yak-trails";
import type {
  BootstrapResult,
  PartySettings,
  PartyCommand,
  PartySummary,
  PlayerProfile,
  PlayerSelection,
  QuickMatchRequest,
  RoomConnection,
  SessionApi,
  TeamBattleRequest,
  PublicTournamentStanding,
  PublicTournamentBracket,
  TournamentAdminAction,
  TournamentAdminDetails,
  TournamentDefinition,
  TournamentResolution,
  TournamentSummary,
} from "@games-monorepo/yak-trails-core";

import { YAK_TRAILS_PREVIEW_IDENTITY_ENABLED } from "../clientConfig";

const PREVIEW_PLAYER_KEY = "yak-trails:preview-player-id";

function previewCredential(): string | undefined {
  if (!YAK_TRAILS_PREVIEW_IDENTITY_ENABLED || typeof window === "undefined") return undefined;
  if (window.location.hash.startsWith("#/yak-trails/admin")) {
    return "preview:admin:preview-school";
  }
  let playerId = window.localStorage.getItem(PREVIEW_PLAYER_KEY);
  if (!playerId) {
    playerId = `guest-${crypto.randomUUID().replace(/-/g, "").slice(0, 12)}`;
    window.localStorage.setItem(PREVIEW_PLAYER_KEY, playerId);
  }
  return `preview:${playerId}:preview-school`;
}

export class ApiSessionApi implements SessionApi {
  private gameSessionToken = "";
  private profile: PlayerProfile | null = null;

  constructor(private readonly apiBase: string) {}

  async bootstrap(credential?: string): Promise<BootstrapResult> {
    const result = await yakTrailsApi.bootstrap(this.apiBase, credential ?? previewCredential());
    this.gameSessionToken = result.gameSessionToken ?? "";
    this.profile = result.profile ?? null;
    return result;
  }

  async startRankedSolo(selection: PlayerSelection): Promise<RoomConnection> {
    return yakTrailsApi.startRankedSolo(this.apiBase, this.requireSession(), selection);
  }

  async joinQuickMatch(request: QuickMatchRequest): Promise<RoomConnection> {
    return yakTrailsApi.joinQuickMatch(this.apiBase, this.requireSession(), request);
  }

  async startTeamBattle(request: TeamBattleRequest): Promise<RoomConnection> {
    return yakTrailsApi.startTeamBattle(this.apiBase, this.requireSession(), request);
  }

  async listTournaments(): Promise<TournamentSummary[]> {
    return yakTrailsApi.listTournaments(this.apiBase, this.requireSession());
  }

  async checkInTournament(tournamentId: string): Promise<{ checkedIn: boolean }> {
    return yakTrailsApi.checkInTournament(this.apiBase, this.requireSession(), tournamentId);
  }

  async resolveTournamentRoom(tournamentId: string): Promise<TournamentResolution> {
    return yakTrailsApi.resolveTournamentRoom(this.apiBase, this.requireSession(), tournamentId);
  }

  async getTournamentStandings(tournamentId: string): Promise<PublicTournamentStanding[]> {
    return yakTrailsApi.getTournamentStandings(this.apiBase, this.requireSession(), tournamentId);
  }

  async getTournamentBracket(tournamentId: string): Promise<PublicTournamentBracket> {
    return yakTrailsApi.getTournamentBracket(this.apiBase, this.requireSession(), tournamentId);
  }

  async listAdminTournaments(): Promise<TournamentSummary[]> {
    return yakTrailsApi.listAdminTournaments(this.apiBase, this.requireSession());
  }

  async getTournamentAdminDetails(tournamentId: string): Promise<TournamentAdminDetails> {
    return yakTrailsApi.getTournamentAdminDetails(this.apiBase, this.requireSession(), tournamentId);
  }

  async previewTournamentDefinition(definition: Partial<TournamentDefinition>, entrantCount: number): Promise<unknown> {
    return yakTrailsApi.previewTournamentDefinition(this.apiBase, this.requireSession(), definition, entrantCount);
  }

  async createTournamentDraft(definition: Partial<TournamentDefinition>, reason: string): Promise<TournamentDefinition> {
    return yakTrailsApi.createTournamentDraft(this.apiBase, this.requireSession(), definition, reason);
  }

  async mutateTournament(
    tournamentId: string,
    action: TournamentAdminAction,
    reason: string,
    scheduledAt?: string,
  ): Promise<unknown> {
    return yakTrailsApi.mutateTournament(
      this.apiBase,
      this.requireSession(),
      tournamentId,
      action,
      reason,
      scheduledAt,
    );
  }

  async overrideTournamentAdvancement(
    tournamentId: string,
    stageId: string,
    entrantIds: string[],
    reason: string,
  ): Promise<{ advanced: number; targetStageId: string }> {
    return yakTrailsApi.overrideTournamentAdvancement(
      this.apiBase,
      this.requireSession(),
      tournamentId,
      stageId,
      entrantIds,
      reason,
    );
  }

  async createParty(settings: PartySettings): Promise<PartySummary> {
    return yakTrailsApi.createParty(this.apiBase, this.requireSession(), settings);
  }

  async joinParty(slug: string): Promise<PartySummary> {
    return yakTrailsApi.joinParty(this.apiBase, this.requireSession(), slug);
  }

  async resolvePartyRoom(slug: string): Promise<RoomConnection> {
    return yakTrailsApi.resolvePartyRoom(this.apiBase, this.requireSession(), slug);
  }

  async commandParty(slug: string, command: PartyCommand): Promise<PartySummary> {
    return yakTrailsApi.commandParty(this.apiBase, this.requireSession(), slug, command);
  }

  async selectSkin(skinId: string): Promise<PlayerProfile> {
    const profile = await yakTrailsApi.selectSkin(
      this.apiBase,
      this.requireSession(),
      skinId,
      this.profile?.profileVersion ?? 0,
    );
    this.profile = profile;
    return profile;
  }

  private requireSession(): string {
    if (!this.gameSessionToken) throw new Error("Online play requires a verified Sidechat session.");
    return this.gameSessionToken;
  }
}
